import { defineStore } from 'pinia';
import axios from 'axios';
import { useMainStore } from '@/stores/useMainStore';
import { useAppStore } from '@/stores/useAppStore';
import { reportFatalError, reportNonFatalError, reportSilentError } from '@/utils/storeErrorReporting';
import { getAuthFailedError, getUserFetchError, getOrganizationsListError, getIndustriesLoadError } from '@/utils/errorMessages';
import parseEmployee from '@/utils/data/parseEmployee';
import { parseVolunteer } from '@/utils/volunteers';
import { toEmployeePayload } from '@/utils/data/toEmployeePayload';
import { hasAcceptedAgreements, hasCompletedOnboarding } from '@/utils/auth/agreements';
import { educatorRoles, METADATA_ROLES, roles } from '@/constants';
import { displayName as getDisplayName } from '@/utils/data/display';
import * as employeesApi from '@/api/employees';
import * as volunteersApi from '@/api/volunteers';

const API_DOMAIN = import.meta.env.VITE_API_URL;

export const useEmployeeStore = defineStore('employee', {
    state: () => ({
        user: {
            employeeId: null,
            data: {},
            organizations: [],
            hasAdmin: false,
        },
        favorites: [],
        favoritesSyncTimestamp: null,
    }),

    getters: {
        /**
         * Returns the current user's ID regardless of role.
         * Educators store their ID in mainStore.user.data (via useEducatorStore.getDEUser).
         * Employees store their ID in this store's own state.
         */
        userId(state) {
            const appStore = useAppStore();
            if (educatorRoles.includes(appStore.mode)) {
                return useMainStore().user.data.id;
            }
            return state.user.data.employeeId;
        },

        /**
         * Returns user email
         */
        userName(state) {
            return state.user.data.email;
        },

        /**
         * Returns user data object
         */
        userData(state) {
            return state.user.data;
        },

        /**
         * Returns formatted display name and avatar for the current user regardless of role.
         * Educators store their profile data in mainStore.user.data (via useEducatorStore).
         * Employees store their profile data in this store's own state.
         */
        userDisplayData(state) {
            const appStore = useAppStore();
            const source = educatorRoles.includes(appStore.mode) ? useMainStore().user.data : state.user.data;

            const { firstName, lastName, preferredName, avatarUrl } = source;

            const name = getDisplayName({
                firstName,
                lastName,
                preferredName,
            });

            return {
                displayName: name,
                avatarUrl,
            };
        },

        /**
         * Returns user organizations
         */
        userOrganizations(state) {
            return state.user.organizations;
        },

        /**
         * Returns filtered admin organizations
         */
        adminOrganizations(state) {
            const { organizations } = state.user;
            return organizations.filter((org) => org.roles.includes(METADATA_ROLES[roles.ORGADMIN_ROLE]));
        },

        /**
         * Returns organizations from viewed employee
         */
        profileEmployeeOrgs() {
            const mainStore = useMainStore();
            if (!mainStore.page.employee.data) return [];
            return mainStore.page.employee.data.organizations;
        },

        /**
         * Returns viewed employee data
         */
        employee() {
            const mainStore = useMainStore();
            return mainStore.page.employee.data;
        },

        /**
         * Factory function to check if employee is favorited
         * @param {string} employeeId - Employee ID to check
         * @returns {boolean}
         */
        isFavorite: (state) => (employeeId) => state.favorites.includes(employeeId),
    },

    actions: {
        /**
         * Update user object with new data
         * @param {Object} data - Data to merge into user object
         */
        updateUser(data) {
            this.$patch({ user: { ...this.user, ...data } });
            // Keep employeeId in sync between user.employeeId and user.data.employeeId
            if (data.employeeId !== undefined) {
                this.user.data = { ...this.user.data, employeeId: data.employeeId };
            }
        },

        /**
         * Update user.data with new data
         * @param {Object} data - Data to merge into user.data
         */
        updateUserData(data) {
            this.user.data = { ...this.user.data, ...data };
        },

        /**
         * Fetch and cache single employee to page.employee
         * @param {Object} payload - Contains token and data.employeeId
         */
        async viewEmployee(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'page', status: true });

                const response = await axios.get(`${API_DOMAIN}/employees/${payload.data.employeeId}`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                mainStore.updatePage({ employee: { data: response.data } });
                mainStore.updateLoading({ key: 'page', status: false });
            } catch (error) {
                reportSilentError(error);
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Fetch and cache employees list to page.employees
         * @param {Object} payload - Contains token and data.organizationId
         */
        async viewEmployees(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'page', status: true });

                const response = await axios.get(`${API_DOMAIN}/organizations/${payload.data.organizationId}/employees`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                mainStore.updatePage({ employees: response.data.records });
                mainStore.updateLoading({ key: 'page', status: false });
            } catch (error) {
                reportSilentError(error);
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Fetch current user's employee data
         * @param {Object} payload - Contains token
         */
        async getEmployee(payload) {
            const mainStore = useMainStore();

            // Validate token before making request
            if (!payload.token) {
                reportFatalError(new Error('Missing authentication token'), getAuthFailedError());
                return;
            }

            try {
                mainStore.updateLoading({ key: 'user', status: true });

                // Fetch industries if not already cached
                const { industries } = mainStore.page;
                if (!industries || industries.length === 0) {
                    try {
                        await this.getIndustries(payload);
                    } catch (error) {
                        reportNonFatalError(error, getOrganizationsListError());
                    }
                }

                const response = await axios.get(`${API_DOMAIN}/employees/${this.user.employeeId}`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const { data, organizations } = parseEmployee(response.data, this.user);
                this.updateUser({ data, organizations });

                mainStore.updateLoading({ key: 'user', status: false });
            } catch (error) {
                reportNonFatalError(error, getUserFetchError());
                mainStore.updateLoading({ key: 'user', status: false });
            }
        },

        /**
         * Save current user's employee data
         * @param {Object} payload - Contains token
         */
        async saveEmployee(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'user', status: true });

                const response = await axios.post(`${API_DOMAIN}/employees/${this.user.employeeId}`, toEmployeePayload(this.user.data), {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const { data, organizations } = parseEmployee(response.data, this.user);
                this.updateUser({ data, organizations });
                mainStore.updatePage({ employee: { data: response.data } });
            } catch (error) {
                reportSilentError(error);
            } finally {
                mainStore.updateLoading({ key: 'user', status: false });
            }
        },

        /**
         * Activate current user's employee profile
         * @param {Object} payload - Contains token
         */
        async activateEmployee(payload) {
            const mainStore = useMainStore();

            // The BE does not enforce agreements, so never send an active record without them.
            if (!hasAcceptedAgreements(this.user.data)) {
                reportSilentError(new Error('Blocked employee activation: an agreement has not been accepted'));
                return;
            }

            try {
                const setToActive = {
                    ...toEmployeePayload(this.user.data),
                    isActive: true,
                };

                mainStore.updateLoading({ key: 'employee_activate', status: true });

                const response = await axios.post(`${API_DOMAIN}/employees/${this.user.employeeId}`, setToActive, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                if (response.status === 200) {
                    const { data, organizations } = parseEmployee(response.data, this.user);

                    this.updateUser({ data, organizations });
                    mainStore.updateApp({ active: hasCompletedOnboarding(data) });
                }

                mainStore.updateLoading({ key: 'employee_activate', status: false });
            } catch (error) {
                reportSilentError(error);
                mainStore.updateLoading({ key: 'employee_activate', status: false });
            }
        },

        /**
         * Mark current user's employee profile as inactive in the BE
         * (used when a required agreement has not been accepted)
         * @param {Object} payload - Contains token
         */
        async deactivateEmployee(payload) {
            try {
                const employee = await employeesApi.deactivateEmployee(this.user.employeeId, this.user.data, payload.token);

                const { data, organizations } = parseEmployee(employee, this.user);
                this.updateUser({ data, organizations });
            } catch (error) {
                // Stay silent for the user, but make the failure findable: the BE keeps is_active = true and
                // the next session retries.
                reportSilentError(new Error('Employee deactivation failed', { cause: error }));
            }
        },

        /**
         * Update current user's avatar
         * @param {Object} payload - Contains token and data.fileContent
         */
        async updateEmployeeAvatar(payload) {
            try {
                const response = await axios.post(`${API_DOMAIN}/employees/${this.user.employeeId}/update-avatar`, payload.data.fileContent, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                        'Content-Type': 'application/json',
                    },
                });

                const { data, organizations } = parseEmployee(response.data, this.user);
                this.updateUser({ data, organizations });
            } catch (error) {
                reportSilentError(error);
            }
        },

        /**
         * Fetch employees with filters/pagination (org admin)
         * @param {Object} payload - Contains token and optional data.params
         */
        async getEmployees(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'page', status: true });

                const response = await employeesApi.fetchAllVolunteers(payload?.data?.params);
                if (!response) return;

                const {
                    records,
                    available_filters: availableFilters,
                    all_active_employees_count: allActiveEmployeesCount,
                    record_count: recordCount,
                } = response;

                mainStore.updatePagination(response);
                mainStore.updatePage({
                    employees: records,
                    availableFilters,
                    totalEmployeesCount: allActiveEmployeesCount,
                    filteredEmployeesCount: recordCount,
                });
            } catch (error) {
                reportSilentError(error);
            } finally {
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Fetch recommended employees (educators) from `GET /v2/volunteers`
         * @param {Object} [filters] - See {@link volunteersApi.fetchVolunteers} for every supported argument
         * @param {() => boolean} [options.isLatestRequest] - Checked after the response arrives; when it returns false the
         *   result is discarded so a superseded request can't overwrite a newer one.
         */
        async getRecommendedEmployees(filters = {}, { isLatestRequest = () => true } = {}) {
            const mainStore = useMainStore();

            mainStore.updateLoading({ key: 'page', status: true });

            try {
                const response = await volunteersApi.fetchVolunteers(
                    filters.limit,
                    filters.offset,
                    filters.organizationId,
                    filters.state,
                    filters.industryCode,
                    filters.subcategoryId,
                    filters.level,
                    filters.tenure,
                    filters.language,
                    filters.areasOfExpertise,
                    filters.favourites,
                    filters.hasCareerShares,
                    filters.preferredState,
                    filters.preferredAreaOfExpertise,
                );
                if (!response || !isLatestRequest()) return;

                // New API reports `total_records_count` and has no `available_filters`.
                const records = response.records.map(parseVolunteer);
                const data = { ...response, records, record_count: response.total_records_count };

                const { record_count: filteredEmployeesCount } = data;

                mainStore.updatePagination(data);
                mainStore.updatePage({
                    employees: records,
                    filteredEmployeesCount,
                });
            } catch (error) {
                reportSilentError(error);
            } finally {
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Fetch employee matches for request
         * @param {Object} payload - Contains token and data with educatorId and requestId
         */
        async getEmployeeMatches(payload) {
            try {
                const response = await axios.get(`${API_DOMAIN}/educators/${payload.data.educatorId}/requests/${payload.data.requestId}/matches`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                return response.data;
            } catch (error) {
                reportSilentError(error);
                return null;
            }
        },

        /**
         * Add/remove employee from favorites (optimistic)
         * @param {Object} payload - Contains employeeId, isFavorite, token
         */
        async toggleFavorite(payload) {
            const { employeeId, isFavorite, token } = payload;

            // Optimistic update
            if (isFavorite) {
                this.favorites.push(employeeId);
            } else {
                this.favorites = this.favorites.filter((id) => id !== employeeId);
            }

            try {
                const method = isFavorite ? 'post' : 'delete';
                const response = await axios({
                    method,
                    url: `${API_DOMAIN}/favorites/${employeeId}`,
                    data: {},
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                // Update sync timestamp on success
                this.favoritesSyncTimestamp = Date.now();

                return response;
            } catch (error) {
                // Rollback optimistic update on error
                if (isFavorite) {
                    this.favorites = this.favorites.filter((id) => id !== employeeId);
                } else {
                    this.favorites.push(employeeId);
                }
                throw error;
            }
        },

        /**
         * Fetch user's favorites list
         * @param {Object} payload - Contains token
         */
        async getFavorites(payload) {
            try {
                const response = await axios.get(`${API_DOMAIN}/favorites`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const favorites = response.data || [];
                this.favorites = favorites;

                return response;
            } catch (error) {
                reportSilentError(error);
                throw error;
            }
        },

        /**
         * Fetch industries and subcategories from API
         * Caches results in mainStore.page.industries
         * @param {Object} payload - Contains token
         */
        async getIndustries(payload) {
            const mainStore = useMainStore();

            mainStore.updateLoading({ key: 'industries', status: true });

            try {
                const response = await axios.get(`${API_DOMAIN}/employees/industries`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                mainStore.updatePage({ industries: response.data });
                mainStore.updateLoading({ key: 'industries', status: false });
                return response.data;
            } catch (error) {
                // Fallback to empty array on error
                // Components can use static fallback from formData.js
                mainStore.updatePage({ industries: [] });
                mainStore.updateLoading({ key: 'industries', status: false });

                reportNonFatalError(error, getIndustriesLoadError());
                throw error;
            }
        },
    },
});
